// Single source of truth for the three restaurants (Reynards, R&R, Brewery Bar)
// used by the Tables page, Room Diary "Send to Restaurant" flow, Events page,
// and the public booking widget. Previously these were duplicated across 3-4
// files with slight drift; consolidating here so that when capacities, sitting
// windows, or venue rules change there's exactly one place to edit.

import { parseLocalDate } from './dateHelpers';

export const RESTAURANT_VENUES = [
  { id: 'reynards', name: 'Reynards Restaurant', capacity: 50 },
  { id: 'randr', name: 'R&R', capacity: 45 },
  { id: 'brewery', name: 'Brewery Bar', capacity: 60 },
];

export const RESTAURANT_SITTINGS = [
  { id: 'breakfast', label: 'Breakfast', time: '9:00 – 12:00', startH: 9, endH: 12, venues: ['randr'] },
  { id: 'lunch',     label: 'Lunch',     time: '12:00 – 16:00', startH: 12, endH: 16, venues: ['reynards', 'randr', 'brewery'] },
  { id: 'evening',   label: 'Evening',   time: '16:00 – 21:00', startH: 16, endH: 21, venues: ['reynards', 'randr', 'brewery'] },
];

// Day-of-week rules & venue policies (updated Sep 2026 per Ronan):
//   R&R      — open every day for breakfast & lunch; evening (16:00-21:00)
//              only at the weekend (Fri/Sat/Sun) — closes at 4pm Mon-Thu
//   Brewery  — evening (16:00-21:00) open every day; lunch only at the
//              weekend (Fri/Sat/Sun) — opens at 4pm Mon-Thu; breakfast not offered
//   Reynards — only bookable for parties of 6+ (every day of the week)

const isWeekend = (dateStr) => {
  const d = parseLocalDate(dateStr);
  if (!d) return false;
  const day = d.getDay();
  return day === 0 || day === 5 || day === 6; // 0 = Sun, 5 = Fri, 6 = Sat
};

/**
 * Return the sittings available for a venue on a given date string.
 * Accounts for R&R's and Brewery's weekday/weekend schedule.
 *
 * Optional `blocks` array (from venue_day_blocks) applies override logic:
 *   - block_type 'open'   → sitting is available even if default rules would close it
 *   - block_type 'closed' → sitting is unavailable even if default rules would open it
 *   - all_day blocks are checked first and apply to all sittings unless a per-sitting
 *     block overrides them
 *
 * Priority order (highest to lowest):
 *   1. Per-sitting 'open' override — always opens that sitting
 *   2. Per-sitting 'closed' block  — always closes that sitting
 *   3. all_day 'open' override     — opens all sittings
 *   4. all_day 'closed' block      — closes all sittings
 *   5. Default day-of-week schedule rules
 */
export function getSittingsForVenueAndDate(venueId, dateStr, blocks = []) {
  const allDayBlock = blocks.find((b) => b.sittingId === 'all_day');

  return RESTAURANT_SITTINGS.filter((s) => {
    if (!s.venues.includes(venueId)) return false;

    // Check for a per-sitting block first (highest priority)
    const sittingBlock = blocks.find((b) => b.sittingId === s.id);
    if (sittingBlock?.blockType === 'open')   return true;
    if (sittingBlock?.blockType === 'closed') return false;

    // Then check all_day block
    if (allDayBlock?.blockType === 'open')   return true;
    if (allDayBlock?.blockType === 'closed') return false;

    // Fall back to default schedule rules
    // Brewery: lunch only at the weekend (Fri/Sat/Sun); evening open every day
    if (venueId === 'brewery' && s.id === 'lunch' && !isWeekend(dateStr)) return false;
    // R&R: evening only at the weekend (Fri/Sat/Sun) — closes at 4pm Mon-Thu
    if (venueId === 'randr' && s.id === 'evening' && !isWeekend(dateStr)) return false;

    return true;
  });
}

/**
 * Minimum covers for a venue. Reynards requires 6+ every day; other venues
 * have no minimum.
 */
export function getMinCoversForVenue(venueId) {
  return venueId === 'reynards' ? 6 : 1;
}

/**
 * Given a time string like "19:30", find which sitting it falls into.
 * Returns null if no sitting covers that hour.
 */
export function guessSittingFromTime(timeStr) {
  if (!timeStr) return null;
  const [h] = String(timeStr).split(':').map(Number);
  return RESTAURANT_SITTINGS.find((s) => h >= s.startH && h < s.endH) || null;
}

/**
 * Check whether a specific time falls inside a given sitting's window.
 */
export function timeInSitting(timeStr, sitting) {
  if (!timeStr || !sitting) return false;
  const [h, m] = String(timeStr).split(':').map(Number);
  const mins = h * 60 + (m || 0);
  return mins >= sitting.startH * 60 && mins < sitting.endH * 60;
}

// ─── SPECIAL EVENT NIGHTS ───
// One-off overrides for a specific venue+date+sitting that don't fit the
// regular day-of-week schedule — e.g. New Year's Eve running two fixed
// sittings at a different capacity instead of the normal continuous
// time range. The sitting still needs to be OPENED for that date via a
// venue_day_blocks 'open' override (this only changes capacity/times once
// it's open, it does not itself override closed/open). Add future one-off
// nights here — this list is the single source of truth mirrored into
// public/booking-widget.html (which can't import this file — see the note
// there) and read by Tables.jsx.
export const SPECIAL_SITTING_OVERRIDES = [
  // NYE 2026 (a Thursday — R&R's evening is normally closed Mon-Thu, so
  // this date also needs a venue_day_blocks 'open' override for
  // randr/2026-12-31/evening to make it bookable at all): two fixed
  // sittings, 6pm & 8pm, 55 covers each instead of the normal continuous
  // 16:00-21:00 range at 45.
  { venueId: 'randr', date: '2026-12-31', sittingId: 'evening', times: ['18:00', '20:00'], capacity: 55 },
];

export function getSpecialSittingOverride(venueId, dateStr, sittingId) {
  return SPECIAL_SITTING_OVERRIDES.find(
    (o) => o.venueId === venueId && o.date === dateStr && o.sittingId === sittingId
  ) || null;
}

/** Venue capacity for a given date/sitting, accounting for any special-event override. */
export function getVenueCapacityForDate(venueId, dateStr, sittingId) {
  const override = getSpecialSittingOverride(venueId, dateStr, sittingId);
  if (override) return override.capacity;
  const venue = RESTAURANT_VENUES.find((v) => v.id === venueId);
  return venue ? venue.capacity : 0;
}

/** Bookable start times for a given venue/date/sitting — either the special-event's fixed times, or every TIME_INTERVAL slot across the sitting's normal window. */
export function getSittingTimeSlots(venueId, dateStr, sittingId, sitting, timeIntervalMinutes) {
  const override = getSpecialSittingOverride(venueId, dateStr, sittingId);
  if (override) return override.times.slice();
  const slots = [];
  for (let h = sitting.startH; h < sitting.endH; h++) {
    for (let m = 0; m < 60; m += timeIntervalMinutes) {
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return slots;
}
